import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Mail, Search, Trash2, UserX } from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import CancelDeletionAction from '../components/CancelDeletionAction';
import {
  useEnforceUser,
  useLookupUserByEmail,
  useRequestUserDeletion,
  useUserDeletion,
  useUserDeletionApproval,
  useUserEnforcement,
} from '../hooks/useAdminOps';
import { ReasonActionBar, StatusChip } from '../components/ui';
import type { EnforcementAction } from '../api/adminOps';
import {
  DELETION_REASONS,
  deletionActionGate,
  deletionLookupState,
  deletionRequestBlocker,
  deletionStateRows,
  EMAIL_MAX_LENGTH,
  formatWhen,
  isDeletionReason,
  isUuid,
  LOOKUP_HELPER_TEXT,
  lookupEmailBlocker,
  lookupFailureMessage,
  reasonConsequence,
  userIdFromSearch,
  waitingPeriodNote,
} from './userDeletion.logic';

const REASON_MAX_LENGTH = 1000;

const ACTIONS: { value: EnforcementAction; label: string; description: string }[] = [
  { value: 'WARN', label: 'Warn', description: 'Record a formal warning on the account' },
  {
    value: 'SUSPEND',
    label: 'Suspend',
    description: 'Disable sign-in and revoke sessions (dual-control approval required)',
  },
  { value: 'REINSTATE', label: 'Reinstate', description: 'Re-enable a suspended account' },
];

function statusTone(status: string): 'ok' | 'warn' | 'crit' | 'neutral' {
  const normalized = status.toLowerCase();
  if (normalized === 'active') return 'ok';
  if (normalized === 'suspended') return 'crit';
  if (normalized === 'warned') return 'warn';
  return 'neutral';
}

function ReasonHint({ reason }: { reason: string }) {
  const consequence = reasonConsequence(reason);
  if (consequence === null) return null;
  if (consequence.tone === 'crit') {
    return (
      <p className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>{consequence.text}</span>
      </p>
    );
  }
  return <p className="mt-2 text-xs text-gray-text">{consequence.text}</p>;
}

/**
 * DM-7185: one account's deletion state, and the two actions on it.
 *
 * A component of its own, keyed on the user id by its parent, so nothing typed
 * for one account (an open form, a half-written note, a "submitted" notice)
 * can survive into the next account that is looked up. Declared at module
 * level: a component declared inside the page would be a new type on every
 * render, remounting its fields and dropping focus on each keystroke.
 */
function AccountDeletionPanel({ userId, canWrite }: { userId: string; canWrite: boolean }) {
  const [formOpen, setFormOpen] = useState(false);
  // Bumped after a successful submit to remount (and so clear) the form.
  const [formVersion, setFormVersion] = useState(0);

  const deletion = useUserDeletion(userId);
  const requestDeletion = useRequestUserDeletion();

  const state = deletion.data?.data ?? null;
  const lookup = deletionLookupState({
    isLoading: deletion.isLoading,
    isError: deletion.isError,
    hasData: deletion.data !== undefined,
  });
  // Only asked once it matters: a deletion can be requested only for an
  // account with nothing scheduled, by a role that may write.
  const nothingScheduled = lookup === 'ready' && state !== null && !state.active;
  const approval = useUserDeletionApproval(userId, nothingScheduled && canWrite);
  const pending = {
    status: deletionLookupState({
      isLoading: approval.isLoading,
      isError: approval.isError,
      hasData: approval.data !== undefined,
    }),
    action: approval.data ?? null,
  };
  const gate = deletionActionGate(lookup, state, canWrite, pending);
  const rows = state === null ? [] : deletionStateRows(state);

  return (
    <section className="glass-card p-4 space-y-4" aria-labelledby="account-deletion-heading">
      <h2
        id="account-deletion-heading"
        className="text-xs font-semibold uppercase tracking-wider text-gray-text/70"
      >
        Account deletion
      </h2>

      <div aria-live="polite" className="space-y-3">
        {lookup === 'error' ? (
          // No role="alert": this sits inside the polite live region above, and
          // nesting the two makes screen readers announce the failure twice.
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-400">{gate.note}</p>
              <button
                type="button"
                className="btn-secondary mt-2"
                onClick={() => deletion.refetch()}
                disabled={deletion.isFetching}
              >
                {deletion.isFetching ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-text">{gate.note}</p>
        )}
        {lookup === 'ready' && pending.status === 'error' && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => approval.refetch()}
            disabled={approval.isFetching}
          >
            {approval.isFetching ? 'Retrying…' : 'Retry'}
          </button>
        )}

        {lookup === 'ready' && rows.length > 0 && state?.request && (
          <>
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              {rows.map(row => (
                <div key={row.label}>
                  <dt className="text-xs text-gray-text">{row.label}</dt>
                  <dd className="text-white break-words">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-gray-text">
              {waitingPeriodNote(state.request.cooling_off_until, new Date())}
            </p>
          </>
        )}
      </div>

      {gate.canCancel && (
        <div className="border-t border-gray-border pt-4">
          <CancelDeletionAction
            userId={userId}
            idPrefix="enforcement"
            disabled={deletion.isFetching}
          />
        </div>
      )}

      {requestDeletion.isSuccess && (
        <p className="text-sm text-yellow-400 border-t border-gray-border pt-4" role="status">
          Submitted. Nothing is scheduled until a different operator approves it on the Approvals
          page — you cannot approve your own request.
        </p>
      )}

      {gate.canRequest && (
        <div className="border-t border-gray-border pt-4 space-y-3">
          {formOpen ? (
            <>
              <ReasonActionBar
                key={formVersion}
                idPrefix="account-deletion"
                reasons={[...DELETION_REASONS]}
                actionLabel="Submit deletion for dual-control approval"
                destructive
                noteRequired
                isPending={requestDeletion.isPending}
                notePlaceholder="Why this account is being deleted. Shown to the approving operator only."
                reasonHint={reason => <ReasonHint reason={reason} />}
                onSubmit={(reason, note) => {
                  // The bar enforces the same rule; checked again here so the
                  // request can never be built from an unvalidated draft.
                  const draft = { reason, note: note ?? '' };
                  if (deletionRequestBlocker(draft) !== null || !isDeletionReason(reason)) return;
                  requestDeletion.mutate(
                    { userId, reason, note: draft.note },
                    {
                      onSuccess: () => {
                        setFormVersion(version => version + 1);
                        setFormOpen(false);
                      },
                    }
                  );
                }}
              />
              <button
                type="button"
                className="btn-ghost w-full"
                onClick={() => setFormOpen(false)}
                disabled={requestDeletion.isPending}
              >
                Keep this account
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="px-4 py-2 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 font-medium transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              Delete account…
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default function UserEnforcement() {
  const { search } = useLocation();
  const [userIdInput, setUserIdInput] = useState(() => userIdFromSearch(search));
  const [lookedUpUserId, setLookedUpUserId] = useState(() => userIdFromSearch(search));
  const [emailInput, setEmailInput] = useState('');
  const [action, setAction] = useState<EnforcementAction>('WARN');
  const [reason, setReason] = useState('');
  const [durationDays, setDurationDays] = useState('7');

  const { hasRole } = useAuth();
  const canEnforce = hasRole('admin', 'admin-operator');

  const enforcement = useUserEnforcement(lookedUpUserId);
  const enforceMutation = useEnforceUser();
  const emailLookup = useLookupUserByEmail();

  // DM-7185: the deletion-request queue links here as /enforcement?user=<id>.
  // Keyed on the search string alone, so typing in the fields below never
  // re-runs it and overwrites what the operator is entering.
  //
  // The URL is followed in both directions. The sidebar's plain /enforcement
  // link keeps this component mounted and only empties the search string; if
  // that left the previous account loaded, the generic page would go on
  // offering suspend and delete controls for a donor nobody asked for.
  const resetEmailLookup = emailLookup.reset;
  useEffect(() => {
    const linked = userIdFromSearch(search);
    setUserIdInput(linked);
    setLookedUpUserId(linked);
    resetEmailLookup();
  }, [search, resetEmailLookup]);

  const trimmedInput = userIdInput.trim();
  const inputIsUuid = isUuid(trimmedInput);

  const clearLoadedAccount = () => {
    setUserIdInput('');
    setLookedUpUserId('');
  };

  const emailBlocker = lookupEmailBlocker(emailInput);
  const lookUpByEmail = (event: FormEvent) => {
    event.preventDefault();
    if (emailBlocker !== null || emailLookup.isPending) return;
    // Nothing stays actionable while the next account is being found, or after
    // the lookup answers "no such account".
    clearLoadedAccount();
    emailLookup.mutate(emailInput.trim(), {
      onSuccess: result => {
        setUserIdInput(result.data.user_id);
        setLookedUpUserId(result.data.user_id);
      },
    });
  };

  const lookUp = () => {
    if (!inputIsUuid) return;
    setLookedUpUserId(trimmedInput);
    // "Account found … loaded below" would now be describing another account.
    if (!emailLookup.isIdle) emailLookup.reset();
  };

  const parsedDuration = Number.parseInt(durationDays, 10);
  const durationValid =
    action !== 'SUSPEND' || (Number.isInteger(parsedDuration) && parsedDuration > 0);
  const canSubmit =
    canEnforce &&
    lookedUpUserId !== '' &&
    reason.trim() !== '' &&
    durationValid &&
    !enforceMutation.isPending;

  const submit = () => {
    if (!canSubmit) return;
    enforceMutation.mutate(
      {
        userId: lookedUpUserId,
        action,
        reason: reason.trim(),
        ...(action === 'SUSPEND' ? { durationDays: parsedDuration } : {}),
      },
      { onSuccess: () => setReason('') }
    );
  };

  const record = enforcement.data?.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <UserX className="w-6 h-6 text-primary" />
          User Enforcement
        </h1>
        <p className="text-gray-text mt-1">
          Look up an account by user id or sign-in email to review its enforcement history and warn,
          suspend, reinstate, or delete it. Suspensions and deletions require a second operator's
          approval.
        </p>
      </div>

      <div className="glass-card p-4">
        <label htmlFor="enforcement-user-id" className="block text-sm text-gray-text mb-2">
          User id (UUID)
        </label>
        <div className="flex gap-2">
          <input
            id="enforcement-user-id"
            type="text"
            value={userIdInput}
            onChange={event => setUserIdInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') lookUp();
            }}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="input flex-1 font-mono text-sm"
          />
          <button
            onClick={lookUp}
            disabled={!inputIsUuid}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            Look up
          </button>
        </div>
        {trimmedInput !== '' && !inputIsUuid && (
          <p className="text-xs text-amber-400 mt-2">Enter a full UUID to look up an account.</p>
        )}

        {/* DM-7185: support requests arrive by email, not by user id. */}
        <form onSubmit={lookUpByEmail} className="mt-4 pt-4 border-t border-gray-border" noValidate>
          <label htmlFor="enforcement-user-email" className="block text-sm text-gray-text mb-2">
            Or find the account by its app sign-in email
          </label>
          <div className="flex gap-2">
            <input
              id="enforcement-user-email"
              type="email"
              autoComplete="off"
              maxLength={EMAIL_MAX_LENGTH}
              value={emailInput}
              onChange={event => {
                setEmailInput(event.target.value);
                // A result for the previous address must not sit beside a new
                // one -- and neither may the previous ACCOUNT: the controls
                // below act on whatever is loaded, and nothing on the page
                // says which address a loaded user id belongs to.
                if (!emailLookup.isIdle) emailLookup.reset();
                clearLoadedAccount();
              }}
              aria-describedby="enforcement-user-email-help enforcement-user-email-result"
              placeholder="donor@example.com"
              className="input flex-1 text-sm"
            />
            <button
              type="submit"
              disabled={emailBlocker !== null || emailLookup.isPending}
              className="btn-secondary flex items-center gap-2 disabled:opacity-50"
            >
              <Mail className="w-4 h-4" aria-hidden="true" />
              {emailLookup.isPending ? 'Finding…' : 'Find account'}
            </button>
          </div>
          <p id="enforcement-user-email-help" className="text-xs text-gray-text mt-2">
            {LOOKUP_HELPER_TEXT} Every lookup is recorded in the audit ledger.
          </p>
          <div id="enforcement-user-email-result" aria-live="polite" className="mt-2 text-sm">
            {emailLookup.isPending && <p className="text-gray-text">Looking for the account…</p>}
            {emailLookup.isError && (
              <p className="text-amber-400">{lookupFailureMessage(emailLookup.error)}</p>
            )}
            {emailLookup.isSuccess && (
              <p className="text-green-400">
                Account found ({emailLookup.data.data.status}
                {emailLookup.data.data.deleted_at
                  ? `, halted for deletion ${formatWhen(emailLookup.data.data.deleted_at)}`
                  : ''}
                ) and loaded below.
              </p>
            )}
          </div>
        </form>
      </div>

      {lookedUpUserId !== '' && (
        <div className="glass-card p-4 space-y-4">
          {enforcement.isLoading ? (
            <p className="text-gray-text">Loading enforcement record…</p>
          ) : enforcement.isError ? (
            <p className="text-red-400">
              Could not load the enforcement record — check the user id and your permissions.
            </p>
          ) : record ? (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-white font-mono text-sm break-all">{record.user_id}</p>
                <StatusChip label={record.status} tone={statusTone(record.status)} />
              </div>

              {canEnforce ? (
                <div className="space-y-3 border-t border-gray-border pt-4">
                  <div className="grid gap-2 sm:grid-cols-3">
                    {ACTIONS.map(candidate => (
                      <button
                        key={candidate.value}
                        onClick={() => setAction(candidate.value)}
                        className={`text-left px-3 py-2 rounded-xl border transition-colors ${
                          action === candidate.value
                            ? 'border-primary bg-primary/10 text-white'
                            : 'border-gray-border text-gray-text hover:text-white'
                        }`}
                      >
                        <span className="block font-medium">{candidate.label}</span>
                        <span className="block text-xs opacity-80">{candidate.description}</span>
                      </button>
                    ))}
                  </div>

                  {action === 'SUSPEND' && (
                    <div>
                      <label
                        htmlFor="enforcement-duration"
                        className="block text-sm text-gray-text mb-1"
                      >
                        Suspension duration (days)
                      </label>
                      <input
                        id="enforcement-duration"
                        type="number"
                        min={1}
                        value={durationDays}
                        onChange={event => setDurationDays(event.target.value)}
                        className="input w-32"
                      />
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="enforcement-reason"
                      className="block text-sm text-gray-text mb-1"
                    >
                      Reason (recorded in the audit ledger)
                    </label>
                    <textarea
                      id="enforcement-reason"
                      value={reason}
                      onChange={event => setReason(event.target.value.slice(0, REASON_MAX_LENGTH))}
                      rows={3}
                      className="input w-full resize-y"
                      placeholder="Why this action is being taken"
                    />
                  </div>

                  <button
                    onClick={submit}
                    disabled={!canSubmit}
                    className="btn-primary disabled:opacity-50"
                  >
                    {enforceMutation.isPending
                      ? 'Submitting…'
                      : action === 'SUSPEND'
                        ? 'Submit for dual-control approval'
                        : `Apply ${action.toLowerCase()}`}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-text border-t border-gray-border pt-4">
                  Your role has read-only access — enforcement actions require the admin or
                  admin-operator role.
                </p>
              )}

              <div className="border-t border-gray-border pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-text/70 mb-2">
                  Enforcement history
                </p>
                {record.history.length === 0 ? (
                  <p className="text-sm text-gray-text">No enforcement events for this account.</p>
                ) : (
                  <ul className="space-y-2">
                    {record.history.map(event => (
                      <li key={event.id} className="text-sm">
                        <span className="text-white">{event.action}</span>{' '}
                        <span className="text-gray-text">
                          — {event.outcome}
                          {event.reason_code ? ` (${event.reason_code})` : ''} ·{' '}
                          {formatDistanceToNow(new Date(event.occurred_at), { addSuffix: true })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Outside the enforcement card on purpose: an account its donor has
          already halted may have no enforcement record to load, and that is
          exactly the account an operator is most likely asked to cancel for. */}
      {lookedUpUserId !== '' && (
        <AccountDeletionPanel key={lookedUpUserId} userId={lookedUpUserId} canWrite={canEnforce} />
      )}
    </div>
  );
}
